<script lang="ts" setup>
import { GpBtn, GpTable, GpTableColumn, type GpTableDataRow, GpTag } from '@gportal/uikit';
import InviteMemberModal from '@panel/components/project/members/InviteMemberModal.vue';
import useGpTableData from '@panel/composables/useGpTableData';
import ProjectSettingsLayout from '@panel/layouts/Layout.Project.Settings.vue';
import { type ServiceError } from 'gpcore-apikit/grpc/types';
import { useProjectStore } from 'gpcore-apikit/store/pinia/project';
import { useSessionStore } from 'gpcore-apikit/store/pinia/session';
import { ProjectMemberRole } from 'gpcore-grpc/gpcore/api/cloud/v1/project_pb';
import { type BasicUser, UserType } from 'gpcore-grpc/gpcore/api/cloud/v1/user_pb';
import TableUser from 'gpcore-uikit/components/Component.Table.User.vue';
import ConfirmModal from 'gpcore-uikit/components/new/ConfirmModal.vue';
import { computed, reactive } from 'vue';
import { useRouter } from 'vue-router';
import EnumTag from 'gpcore-uikit/components/Component.EnumTag.vue';
import useProjectViewTitle from '@panel/composables/useProjectViewTitle';
import useNotifications from 'gpcore-uikit/composables/useNotifications';

useProjectViewTitle('Members');

interface MembersTableRow extends GpTableDataRow {
  selectable: boolean;
  key: string;
  user: BasicUser;
  isSessionUser: boolean;
  isOwner: boolean;
  role: { confirmed: boolean; role: ProjectMemberRole };
}

const sessionStore = useSessionStore();
const session = computed(() => sessionStore.session);

const projectStore = useProjectStore();
const project = computed(() => projectStore.project);
const membership = computed(() => projectStore.currentUserMembership);

const members = computed(() => project.value.members);
const { tableData, selectedRows: toDelete } = useGpTableData(members, (data) =>
  data.map((member) => ({
    // do not allow the project owner to remove themselves
    selectable: member.role !== ProjectMemberRole.OWNER,
    key: member.user!.id!,
    user: member.user,
    isSessionUser: member.user!.id! === session.value.user.id,
    isOwner: member.role === ProjectMemberRole.OWNER,
    role: {
      confirmed: member.confirmed,
      role: member.role,
    },
  })),
);

const inviteModal = reactive({
  show: false,
});

const destroyModal = reactive({
  show: false,
});
const doDelete = (row?: MembersTableRow) => {
  if (row != null) toDelete.value = [row] as MembersTableRow[];
  if (toDelete.value.length === 0) return;

  destroyModal.show = true;
};
const onDelete = () => {
  if (!destroyModal.show) {
    return;
  }

  toDelete.value.forEach((row, index: number) => {
    if (row.key !== session.value.user.id) {
      projectStore.removeMember(row.key).then(() => {
        toDelete.value = toDelete.value.slice(index, 1);
      });
    }
  });

  destroyModal.show = false;
};

const leaveModal = reactive({
  show: false,
});
const router = useRouter();
const { addNotification } = useNotifications();
const onLeave = () => {
  if (!leaveModal.show) {
    return;
  }

  projectStore
    .leaveProject(project.value.id)
    .then(() => {
      router.push({ name: 'Home' });
      leaveModal.show = false;
    })
    .catch((error: ServiceError) => {
      addNotification({ message: error.message, type: 'danger' });
    });
};
</script>

<template>
  <ProjectSettingsLayout v-playwright="'project-members-view'">
    <template #buttons>
      <template v-if="membership && membership.role === ProjectMemberRole.OWNER">
        <GpBtn
          btn-type="danger"
          size="sm"
          outline
          :disabled="toDelete.length === 0"
          :icon="['fas', 'fa-trash']"
          label="Remove"
          @click="doDelete()"
        />
        <GpBtn
          size="sm"
          :icon="['fas', 'fa-envelope']"
          label="Invite"
          @click="inviteModal.show = true"
        />
      </template>
      <template v-else>
        <GpBtn
          btn-type="danger"
          size="sm"
          outline
          @click="leaveModal.show = true"
          :icon="['fas', 'fa-door-open']"
          label="Leave"
        />
      </template>
    </template>

    <template #content>
      <ConfirmModal
        :show="destroyModal.show"
        confirm-text="Remove"
        confirm-type="danger"
        @close="destroyModal.show = false"
        @confirm="onDelete"
      />
      <ConfirmModal
        :show="leaveModal.show"
        confirm-text="Leave"
        confirm-type="danger"
        @close="leaveModal.show = false"
        @confirm="onLeave"
      />

      <InviteMemberModal :show="inviteModal.show" @close="inviteModal.show = false" />

      <GpTable v-if="tableData" :data="tableData" v-model:selected-rows="toDelete">
        <GpTableColumn
          v-if="membership && membership.role === ProjectMemberRole.OWNER"
          field="checkbox"
          selection="checkbox"
          :column-style="{ verticalAlign: 'middle' }"
          :header-style="{ width: '5rem' }"
        />
        <GpTableColumn field="user" label="User">
          <template #body="{ data, row }: { data: BasicUser; row: MembersTableRow }">
            <TableUser
              :user="{
                id: data.id,
                avatarUrl: data.avatarUrl,
                fullName: data.fullName,
                email: data.email,
                username: data.username,
              }"
              :is-session-user="row.isSessionUser"
            />
          </template>
        </GpTableColumn>

        <GpTableColumn field="type" label="">
          <template #body="{ row }: { row: MembersTableRow }">
            <EnumTag :enum-map="UserType" :enum-value="row.user.type" prefix="USER_TYPE_" />
          </template>
        </GpTableColumn>

        <GpTableColumn field="role" label="Role" :column-style="{ verticalAlign: 'middle' }">
          <template #body="{ data }: { data: MembersTableRow['role'] }">
            <EnumTag
              v-if="data.confirmed"
              :enum-map="ProjectMemberRole"
              :enum-value="data.role"
              prefix="PROJECT_MEMBER_ROLE_"
            />

            <GpTag v-if="!data.confirmed" tag-type="warning" size="xs" label="PENDING INVITE" />
          </template>
        </GpTableColumn>

        <GpTableColumn field="actions">
          <template #body="{ row }: { row: MembersTableRow }">
            <div class="float-end">
              <GpBtn
                v-if="!row.isSessionUser && !row.isOwner"
                size="sm"
                btn-type="danger"
                label="Remove"
                :icon="['fas', 'fa-trash']"
                @click="doDelete(row)"
              >
              </GpBtn>
            </div>
          </template>
        </GpTableColumn>
      </GpTable>
    </template>
  </ProjectSettingsLayout>
</template>
